import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { FaTrophy, FaTimes, FaMusic, FaPlay, FaStop } from 'react-icons/fa';
import { usePlayer } from './PlayerContext';
import './RankingBattle.css';

// Generate all unique pairs from songs array
function generateMatchups(songs) {
    const pairs = [];
    for (let i = 0; i < songs.length; i++) {
        for (let j = i + 1; j < songs.length; j++) {
            pairs.push([songs[i], songs[j]]);
        }
    }
    // Shuffle pairs
    for (let i = pairs.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
    }
    // Limit to reasonable number of matchups
    const maxMatchups = Math.min(pairs.length, Math.max(10, Math.floor(songs.length * 1.5)));
    return pairs.slice(0, maxMatchups);
}

// Calculate ranking from wins
function calculateRanking(songs, wins) {
    return [...songs].sort((a, b) => (wins[b.id] || 0) - (wins[a.id] || 0));
}

// Calculate similarity percentage
function calculateSimilarity(originalRanking, userRanking) {
    let matches = 0;
    const total = originalRanking.length;

    originalRanking.forEach((song, idx) => {
        const userIdx = userRanking.findIndex(s => s.id === song.id);
        // Give partial credit for nearby positions
        const distance = Math.abs(idx - userIdx);
        if (distance === 0) matches += 1;
        else if (distance === 1) matches += 0.5;
        else if (distance === 2) matches += 0.25;
    });

    return Math.round((matches / total) * 100);
}

export default function RankingBattle({ isOpen, onClose, songs, onApplyRanking }) {
    const [currentMatchup, setCurrentMatchup] = useState(0);
    const [wins, setWins] = useState({});
    const [isComplete, setIsComplete] = useState(false);
    const [previewTrackId, setPreviewTrackId] = useState(null);
    const [isClosing, setIsClosing] = useState(false);
    const [shouldRender, setShouldRender] = useState(isOpen);
    const [modalKey, setModalKey] = useState(0);

    const { isPlaying: mainIsPlaying, pause: pauseMain, resume: resumeMain, spotifyApi } = usePlayer();
    const wasPlayingRef = useRef(false);
    const previewContainerRef = useRef(null);
    const previewControllerRef = useRef(null);

    // Keep ref in sync with playing state
    const mainIsPlayingRef = useRef(mainIsPlaying);
    useEffect(() => {
        mainIsPlayingRef.current = mainIsPlaying;
    }, [mainIsPlaying]);

    // Initialize state when opening
    useEffect(() => {
        if (isOpen) {
            setCurrentMatchup(0);
            setWins({});
            setIsComplete(false);
            setPreviewTrackId(null);
            wasPlayingRef.current = mainIsPlayingRef.current;
        }
    }, [isOpen]);

    // Handle preview player creation with API
    useEffect(() => {
        if (!previewTrackId || !spotifyApi || !previewContainerRef.current) return;

        // Clear previous content manually
        const container = previewContainerRef.current;
        container.innerHTML = '';

        // Create a dedicated element for the API to replace/use
        const element = document.createElement('div');
        container.appendChild(element);

        const options = {
            uri: `spotify:track:${previewTrackId}`,
            width: '100%',
            height: '80',
        };

        const callback = (EmbedController) => {
            previewControllerRef.current = EmbedController;
            EmbedController.play();
        };

        spotifyApi.createController(element, options, callback);

        return () => {
            previewControllerRef.current = null;
            // Cleanup DOM
            if (container) {
                container.innerHTML = '';
            }
        };
    }, [previewTrackId, spotifyApi]);

    const matchups = useMemo(() => {
        if (isOpen) {
            return generateMatchups(songs);
        }
        return [];
    }, [isOpen, songs]);

    // Start preview for a song
    const startPreview = useCallback((trackId, e) => {
        e.stopPropagation(); // Don't trigger vote
        if (!wasPlayingRef.current && mainIsPlaying) {
            wasPlayingRef.current = true;
        }
        if (mainIsPlaying) {
            pauseMain();
        }
        setPreviewTrackId(trackId);
    }, [mainIsPlaying, pauseMain]);

    // Stop preview
    const stopPreview = useCallback(() => {
        setPreviewTrackId(null);
    }, []);

    // Handle closing - stop preview and resume main if it was playing
    const handleClose = useCallback(() => {
        setPreviewTrackId(null);
        if (wasPlayingRef.current) {
            resumeMain();
        }
        onClose();
    }, [onClose, resumeMain]);

    const handleVote = useCallback((winner) => {
        // Stop any preview
        setPreviewTrackId(null);

        // Resume main player if it was playing
        if (wasPlayingRef.current) {
            resumeMain();
        }

        setWins(prev => ({
            ...prev,
            [winner.id]: (prev[winner.id] || 0) + 1
        }));

        if (currentMatchup + 1 >= matchups.length) {
            setIsComplete(true);
        } else {
            setCurrentMatchup(prev => prev + 1);
        }
    }, [currentMatchup, matchups.length, resumeMain]);

    const userRanking = useMemo(() => {
        if (isComplete) {
            return calculateRanking(songs, wins);
        }
        return [];
    }, [isComplete, songs, wins]);

    const similarity = useMemo(() => {
        if (isComplete) {
            return calculateSimilarity(songs, userRanking);
        }
        return 0;
    }, [isComplete, songs, userRanking]);

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setIsClosing(false);
        } else if (shouldRender) {
            // Stop any preview when starting to close
            setPreviewTrackId(null);
            setIsClosing(true);
            const timer = setTimeout(() => {
                setShouldRender(false);
                setIsClosing(false);
                setModalKey(prev => prev + 1); // Force fresh mount on next open
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen, shouldRender]);

    if (!shouldRender) return null;

    const currentPair = matchups[currentMatchup];

    return (
        <div key={modalKey} className={`battle-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
            <div className={`battle-modal ${isClosing ? 'closing' : ''}`} onClick={e => e.stopPropagation()}>
                <button className="battle-close" onClick={handleClose}>
                    <FaTimes size={24} />
                </button>

                {!isComplete ? (
                    <>
                        <div className="battle-header">
                            <h2>Battle of the Bands</h2>
                            {currentMatchup === 0 && (
                                <p className="battle-reference">Trollhunters reference lol</p>
                            )}
                            <p className="battle-progress">
                                Match {currentMatchup + 1} of {matchups.length}
                            </p>
                            <div className="battle-progress-bar">
                                <div
                                    className="battle-progress-fill"
                                    style={{ width: `${((currentMatchup) / matchups.length) * 100}%` }}
                                />
                            </div>
                        </div>

                        <div className="battle-arena">
                            {currentPair && (
                                <>
                                    <div className="battle-card-wrapper">
                                        <div
                                            className="battle-card left"
                                            onClick={() => handleVote(currentPair[0])}
                                        >
                                            <div className="battle-card-art">
                                                {currentPair[0].thumbnail ? (
                                                    <img src={currentPair[0].thumbnail} alt="" />
                                                ) : (
                                                    <FaMusic size={40} />
                                                )}
                                            </div>
                                            <div className="battle-card-info">
                                                <div className="battle-card-title">{currentPair[0].title}</div>
                                                <div className="battle-card-artist">{currentPair[0].artist}</div>
                                            </div>
                                        </div>
                                        <button
                                            className={`preview-btn ${previewTrackId === currentPair[0].id ? 'playing' : ''}`}
                                            onClick={(e) => previewTrackId === currentPair[0].id ? stopPreview() : startPreview(currentPair[0].id, e)}
                                        >
                                            {previewTrackId === currentPair[0].id ? <FaStop size={12} /> : <FaPlay size={12} />}
                                            {previewTrackId === currentPair[0].id ? 'Stop' : 'Preview'}
                                        </button>
                                    </div>

                                    <div className="battle-vs">VS</div>

                                    <div className="battle-card-wrapper">
                                        <div
                                            className="battle-card right"
                                            onClick={() => handleVote(currentPair[1])}
                                        >
                                            <div className="battle-card-art">
                                                {currentPair[1].thumbnail ? (
                                                    <img src={currentPair[1].thumbnail} alt="" />
                                                ) : (
                                                    <FaMusic size={40} />
                                                )}
                                            </div>
                                            <div className="battle-card-info">
                                                <div className="battle-card-title">{currentPair[1].title}</div>
                                                <div className="battle-card-artist">{currentPair[1].artist}</div>
                                            </div>
                                        </div>
                                        <button
                                            className={`preview-btn ${previewTrackId === currentPair[1].id ? 'playing' : ''}`}
                                            onClick={(e) => previewTrackId === currentPair[1].id ? stopPreview() : startPreview(currentPair[1].id, e)}
                                        >
                                            {previewTrackId === currentPair[1].id ? <FaStop size={12} /> : <FaPlay size={12} />}
                                            {previewTrackId === currentPair[1].id ? 'Stop' : 'Preview'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Hidden preview player */}
                        {/* Hidden preview player */}
                        {previewTrackId && (
                            <div className="preview-player-container">
                                <div ref={previewContainerRef} />
                            </div>
                        )}

                        <p className="battle-hint">Click a card to pick the winner</p>
                    </>
                ) : (
                    <div className="battle-results">
                        <div className="battle-results-header">
                            <FaTrophy className="trophy-icon" size={48} />
                            <h2>Ranking Complete!</h2>
                            <p className="similarity-score">
                                You match <span className="score">{similarity}%</span> with Divyesh's taste
                            </p>
                        </div>

                        <div className="rankings-comparison">
                            <div className="ranking-column">
                                <h3>Your Ranking</h3>
                                <ol className="ranking-list">
                                    {userRanking.map((song, idx) => (
                                        <li key={song.id} className="ranking-item">
                                            <span className="rank-num">{idx + 1}</span>
                                            <span className="rank-title">{song.title}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>

                            <div className="ranking-column">
                                <h3>Divyesh's Ranking</h3>
                                <ol className="ranking-list">
                                    {songs.map((song, idx) => (
                                        <li key={song.id} className="ranking-item">
                                            <span className="rank-num">{idx + 1}</span>
                                            <span className="rank-title">{song.title}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>

                        <div className="battle-actions">
                            <button className="apply-ranking-btn" onClick={() => {
                                if (onApplyRanking) {
                                    onApplyRanking(userRanking);
                                }
                                onClose();
                            }}>
                                Apply My Ranking
                            </button>
                            <button className="battle-again-btn" onClick={() => {
                                setCurrentMatchup(0);
                                setWins({});
                                setIsComplete(false);
                            }}>
                                Battle Again
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
